import { Component } from '@theme/component';
import { eventBus, BackInStockRequestedEvent } from '@theme/events';
import { CartActions, CartAddEvent } from '@theme/cart--state--actions';
import { cartStore } from '@theme/cart--state--store';
import { runEffects } from '@theme/effect-registry';

/**
 * Cart Variant Select — web component for quick add by option.
 *
 * Renders inline variant buttons. Each button adds that specific variant to cart on click.
 * Self-contained: no dependency on shared--content--product-card.js.
 *
 * Architecture (ngrx-style):
 * - Emits CartAddEvent (action) to request adding item to cart
 * - Calls runEffects() to invoke declarative effect block handlers (dispatch/success/error)
 * - Listens to cart:item-added and cart:add-failed (results) for per-button state feedback
 * - Exposes state via per-button `data-state` attribute: '' | 'loading' | 'success' | 'error'
 *
 * @attr {string} data-product-id - Product ID for event metadata
 * @attr {string} data-product-url - Product URL for preview link
 * @attr {string} data-product-title - Product title for preview
 * @attr {string} data-product-image - Featured image URL for preview
 * @attr {string} data-action - Action identifier ("cart:add")
 *
 * @extends {Component}
 */
class CartVariantSelect extends Component {
	/** @type {(() => void)[]} */
	#unsubscribes = [];

	/** @type {string|null} */
	#pendingVariantId = null;

	/** @type {HTMLButtonElement|null} */
	#pendingButton = null;

	connectedCallback() {
		super.connectedCallback();
		this.#setupResultListeners();
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		this.#pendingVariantId = null;
		this.#pendingButton = null;

		for (const unsub of this.#unsubscribes) {
			unsub();
		}
		this.#unsubscribes = [];
	}

	/**
	 * Click handler for variant buttons — bound via on:click="/handleVariantClick" in Liquid.
	 * @param {Event} event
	 */
	handleVariantClick(event) {
		event.preventDefault();
		event.stopPropagation();

		const button = event.target.closest('[data-selected-variant]');
		if (!button) return;

		const variantId = button.dataset.selectedVariant;
		if (!variantId) return;

		// Unavailable variant → back-in-stock request
		if (button.hasAttribute('data-unavailable')) {
			this.#requestBackInStock(variantId, button);
			return;
		}

		// Already adding → ignore
		if (this.#pendingButton) return;

		// Quick add drawer effect: open drawer instead of adding to cart
		if (this.querySelector('[data-action-effect="cart-quick-add-drawer"], [data-action-effect="aeyde-cart-quick-add-drawer"]')) {
			runEffects(this, 'dispatch', {
				action: 'cart:add',
				payload: { variantId, quantity: 1, productId: this.dataset.productId },
				preview: this.#extractPreviewData(button),
			});
			return;
		}

		// Stock validation: check cart store quantity against inventory limit
		const maxQty = parseInt(button.dataset.inventoryQuantity, 10);
		if (!isNaN(maxQty) && cartStore.getVariantQuantity(variantId) >= maxQty) {
			button.dataset.state = 'error';

			// Still run effects — opens drawer with existing item at top
			runEffects(this, 'dispatch', {
				action: 'cart:add',
				payload: { variantId, quantity: 1, productId: this.dataset.productId },
				preview: this.#extractPreviewData(button),
			});
			return;
		}

		this.#addToCart(variantId, button);
	}

	/**
	 * Emit CartAddEvent + run effect handlers.
	 * @param {string} variantId
	 * @param {HTMLButtonElement} button
	 */
	#addToCart(variantId, button) {
		this.#pendingVariantId = variantId;
		this.#pendingButton = button;
		button.dataset.state = 'loading';

		const preview = this.#extractPreviewData(button);

		// 1. Emit action: request to add item
		eventBus.emit(
			new CartAddEvent({
				data: {
					variantId,
					quantity: 1,
					source: 'variant-picker',
					productId: this.dataset.productId,
				},
			})
		);

		// 2. Run effect handlers (declarative markers inside this element)
		const detail = {
			action: 'cart:add',
			payload: { variantId, quantity: 1, productId: this.dataset.productId },
			preview,
		};
		runEffects(this, 'dispatch', detail);
	}

	/**
	 * Build preview data from data attributes and button text.
	 * @param {HTMLButtonElement} button
	 * @returns {{ title: string, price: string, imageSrc: string, productUrl: string, variantTitle: string }}
	 */
	#extractPreviewData(button) {
		const card = this.closest('product-card');
		const price = card?.querySelector('.product-card__price--current')?.textContent?.trim() ?? '';
		const cardImg = card?.querySelector('.product-card__image');

		return {
			title: this.dataset.productTitle ?? '',
			price,
			imageSrc: cardImg?.currentSrc || cardImg?.src || this.dataset.productImage || '',
			productUrl: this.dataset.productUrl ?? '',
			variantTitle: button.textContent?.trim() ?? '',
		};
	}

	/**
	 * Dispatch back-in-stock notification request.
	 * @param {string} variantId
	 * @param {HTMLButtonElement} button
	 */
	#requestBackInStock(variantId, button) {
		eventBus.emit(
			new BackInStockRequestedEvent({
				data: {
					productTitle: this.dataset.productTitle ?? '',
					productUrl: this.dataset.productUrl ?? '',
					productImage: this.dataset.productImage ?? '',
					productId: this.dataset.productId ?? '',
					productColor: this.dataset.productColor ?? '',
					variantId,
					variantTitle: button.textContent?.trim() ?? '',
					triggerElement: button,
				},
			})
		);
	}

	/**
	 * Listen for cart result events to update per-button state feedback.
	 */
	#setupResultListeners() {
		// Success
		this.#unsubscribes.push(
			eventBus.on(CartActions.itemAdded, (event) => {
				const { variantId } = event.detail.data;
				if (String(variantId) !== String(this.#pendingVariantId)) return;

				const button = this.#pendingButton;
				if (button) {
					button.dataset.state = 'success';
					setTimeout(() => {
						button.dataset.state = '';
					}, 1000);
				}

				runEffects(this, 'success', { action: 'cart:item-added', payload: { variantId } });

				this.#pendingButton = null;
				this.#pendingVariantId = null;
			})
		);

		// Failure
		this.#unsubscribes.push(
			eventBus.on(CartActions.addFailed, (event) => {
				const { variantId, message } = event.detail.data;
				if (String(variantId) !== String(this.#pendingVariantId)) return;

				const button = this.#pendingButton;
				if (button) {
					button.dataset.state = 'error';
					setTimeout(() => {
						button.dataset.state = '';
					}, 2000);
				}

				runEffects(this, 'error', { action: 'cart:add-failed', payload: { variantId, message } });

				this.#pendingButton = null;
				this.#pendingVariantId = null;
			})
		);
	}
}

if (!customElements.get('cart-variant-select')) {
	customElements.define('cart-variant-select', CartVariantSelect);
}
